import axiosClient from './axiosClient';

export interface ExcelMappingField {
  id?: number;
  targetField: string;
  targetFieldLabel: string;
  excelColumn?: string;
  isRequired?: boolean;
  defaultValue?: string;
  source?: 'excel' | 'nextcloud' | 'constant';
  constantValue?: string;
  // UI-only: indica si este campo es manual (nombre/etiqueta libres)
  custom?: boolean;
}

export interface ExcelMappingConfig {
  id?: number;
  name: string;
  description?: string;
  excelFileName: string;
  excelFilePath: string;
  excelColumns: string[];
  isActive?: boolean;
  isDefault?: boolean;
  userId?: number;
  createdAt?: Date;
  updatedAt?: Date;
  fields?: ExcelMappingField[];
  reportType?: string | null;
  reportTemplate?: string | null;
  uploadFileTypes?: string[] | null; // Array con 'pdf' y/o 'docx', por defecto ['pdf']
}

export interface TargetField {
  targetField: string;
  targetFieldLabel: string;
  isRequired: boolean;
}

export interface ExcelUploadResponse {
  success: boolean;
  message: string;
  data?: {
    fileName: string;
    filePath: string;
    columns: string[];
    preview: any[];
  };
  error?: string;
}

export interface ExcelMappingResponse {
  success: boolean;
  message?: string;
  data?: ExcelMappingConfig | ExcelMappingConfig[];
  error?: string;
}

export interface PreviewResponse {
  success: boolean;
  message?: string;
  data?: any[];
  error?: string;
}

export const excelMappingApi = {
  createConfig: async (config: Omit<ExcelMappingConfig, 'id' | 'userId' | 'createdAt' | 'updatedAt'>) => {
    const response = await axiosClient.post('/excel-mapping', config);
    return response.data;
  },

  getConfigs: async () => {
    const response = await axiosClient.get('/excel-mapping');
    return response.data;
  },

  getConfigById: async (id: number) => {
    const response = await axiosClient.get(`/excel-mapping/${id}`);
    return response.data;
  },

  updateConfig: async (id: number, config: Partial<ExcelMappingConfig>) => {
    const response = await axiosClient.patch(`/excel-mapping/${id}`, config);
    return response.data;
  },

  deleteConfig: async (id: number) => {
    const response = await axiosClient.delete(`/excel-mapping/${id}`);
    return response.data;
  },

  getTargetFields: async () => {
    const response = await axiosClient.get('/excel-mapping/target-fields');
    return response.data;
  },

  getReportTypes: async () => {
    // Llamar directamente al servicio externo usando el perfil activo
    const { getActiveConfigProfile } = await import('./configProfiles');
    const prof = await getActiveConfigProfile();
    const apinformesUrl: string | undefined = prof?.apinformesUrl || undefined;
    if (apinformesUrl) {
      const base = apinformesUrl.replace(/\/$/, '');
      const response = await fetch(`${base}/reportgenerator/types`, { method: 'GET' });
      const data = await response.json();
      return { success: true, data };
    }
    // Fallback al backend (proxy)
    const response = await axiosClient.get('/reports/types');
    return response.data;
  },

  getTemplatesByType: async (type: string) => {
    // Llamar directamente al servicio externo usando el perfil activo
    const { getActiveConfigProfile } = await import('./configProfiles');
    const prof = await getActiveConfigProfile();
    const apinformesUrl: string | undefined = prof?.apinformesUrl || undefined;
    if (apinformesUrl) {
      const base = apinformesUrl.replace(/\/$/, '');
      const response = await fetch(`${base}/template-generator/templates-by-type/${encodeURIComponent(type)}`, { method: 'GET' });
      const data = await response.json();
      return { success: true, data };
    }
    // Fallback al backend (proxy)
    const response = await axiosClient.get(`/reports/templates/${encodeURIComponent(type)}`);
    return response.data;
  },

  getPreview: async (id: number): Promise<PreviewResponse> => {
    const response = await axiosClient.get(`/excel-mapping/${id}/preview`);
    return response.data as PreviewResponse;
  },

  existsGeneratedFor: async (nif: string, opts?: { template?: string; type?: string }) => {
    const params = new URLSearchParams();
    params.set('nif', nif);
    if (opts?.template) params.set('template', opts.template);
    if (!opts?.template && opts?.type) params.set('type', opts.type);
    const response = await axiosClient.get(`/upload/documents/exists?${params.toString()}`);
    return response.data;
  },

  uploadExcelForMapping: async (file: File) => {
    const formData = new FormData();
    formData.append('excelFile', file);
    const response = await axiosClient.post('/excel-mapping/upload-excel', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  previewMappedData: async (configId: number, file: File) => {
    const formData = new FormData();
    formData.append('excelFile', file);
    const response = await axiosClient.post(`/excel-mapping/${configId}/preview`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};
